'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import { Heading } from '@/components/ui/Heading';
import Link from 'next/link';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { ArrowLeftIcon } from 'lucide-react';

// وقتی آیتم‌ها کمن، عرض هر اسلاید بزرگ‌تر می‌شه تا ردیف رو مساوی پر کنه؛ ولی
// اگه جا نباشه (زوم بالا، صفحه‌ی باریک) به‌جای شکستن خط (که با CSS grid پیش
// می‌اومد)، به حداقل‌عرض می‌چسبه و خودِ سوییپر اسکرول‌پذیرش می‌کنه.
const MAX_VISIBLE = 5;
const MIN_ITEM_WIDTH = 240;
const GAP = 16;

export default function SwiperList({ data }: SwiperListProps) {
  const { dictionary } = useDictionary();

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const visibleCount = Math.min(items.length, MAX_VISIBLE) || 1;
  const slideWidth = `max(${MIN_ITEM_WIDTH}px, calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount}))`;

  const renderCard = (item: (typeof items)[number]) => (
    <div>
      <div className="w-full aspect-[5/6] mb-2 relative overflow-hidden rounded-md">
        {(item.image?.url ||
          item.image_info?.url) && (
            <Image
              src={
                item.image?.url ||
                item.image_info?.url
              }
              alt={item.title || ''}
              fill
              className="object-cover rounded-md"
            />
          )}
      </div>
      <Heading level={4}>{item.title}</Heading>
      <p className="text-primary-600">{item.description}</p>
    </div>
  );

  return (
    <div className="w-full col-span-12">
      <div className="flex justify-between items-center mb-2 max-md:flex-col max-md:items-start">
        <div>
          <Heading level={3}>{data.title}</Heading>
          <p className="text-primary-600">{data.description}</p>
        </div>
        {data.link && (
          <Link className="text-primary-600 flex items-center gap-1" href={data.link}>
            {dictionary.ui?.pages.seeAll}
            <ArrowLeftIcon className="stroke-1 size-4 ltr:rotate-180" />
          </Link>
        )}
      </div>
      <Swiper
        modules={[Scrollbar]}
        spaceBetween={GAP}
        slidesPerView="auto"
        scrollbar={{
          draggable: true,
          dragSize: 100,
        }}
        className="w-full"
        style={{
          paddingBottom: '70px',
        }}
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={index}
            style={{ width: slideWidth, height: '360px' }}
          >
            {item.link ? <Link href={item.link}>{renderCard(item)}</Link> : renderCard(item)}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
